import React, { useEffect, useMemo, useState } from 'react';
import KPICard, { KPIGrid } from '../KPICard';
import DataTable from '../DataTable';
import LasLogChart from '../charts/LasLogChart';
import { useAssistantPageExtras } from '../assistant/AssistantContext';
import { evidenceApi } from '../../auth/api';
import { track } from '../../lib/track';
import { navigateToWell } from '../../utils/wellIdentity';
import { useDeal } from '../../deals/DealContext';
import { dealDataUrl } from '../../deals/registry';

const KIND_LABEL = {
  'openhole-quad': 'Open-hole quad',
  gamma: 'Gamma',
  survey: 'Survey / gyro',
  mudlog: 'Mudlog',
  stimlog: 'Stimlog',
  'cased-cbl': 'Cased-hole CBL',
  porosity: 'Porosity',
  other: 'Other',
  'log-pdf': 'Log PDF',
  'raster-tif': 'Raster TIFF',
  'completion-pdf': 'Completion PDF',
  'doc-pdf': 'Document PDF',
  'raster-img': 'Raster image',
};

/**
 * Authenticated raster/PDF log evidence viewer (SPEC-55). Streams allowlisted
 * private files through the auth server (internal desk only) — never converts
 * to LAS, never exposes an unauthenticated URL.
 */
export function EvidenceLogs({ logEvidence, dashboardOrigin = 'geoscience' }) {
  const { dealId } = useDeal();
  const [msg, setMsg] = useState(null);
  const files = logEvidence?.files || [];
  if (!files.length) return null;

  async function openEvidence(row) {
    setMsg(`Opening ${row.fileName}…`);
    const openedAt = Date.now();
    const baseProps = {
      file_id: row.id,
      kind: row.kind,
      well: row.well,
      file_name: row.fileName,
      mime: row.mime || row.contentType || undefined,
      dashboard_origin: dashboardOrigin,
      field: row.field || undefined,
      deal_id: dealId,
    };
    try {
      const url = await evidenceApi.open(row.id, dealId);
      const win = window.open(url, '_blank', 'noopener');
      if (!win) setMsg(`Popup blocked — allow popups to view ${row.fileName}.`);
      else setMsg(null);
      track('evidence_opened', {
        ...baseProps,
        id: row.id,
        open_latency_ms: Date.now() - openedAt,
        popup_blocked: !win,
      });
    } catch (e) {
      track('evidence_viewer_error', {
        ...baseProps,
        status: e.status || undefined,
        error: String(e.message || e).slice(0, 160),
      });
      if (e.status === 503) {
        setMsg('VDR root not configured on this host — set ENERGY_VDR_ROOT (see README).');
      } else if (e.status === 403 || e.status === 401) {
        setMsg('Not authorized — evidence logs are internal desk only.');
      } else if (e.status === 404) {
        setMsg('File not found on host (indexed but not synced).');
      } else {
        setMsg(`Could not open evidence (${e.status || 'error'}).`);
      }
    }
  }

  return (
    <section className="dashboard-section">
      <div className="section-title">
        Log evidence (raster / PDF)
        <span className="section-meta">
          {' '}
          · {logEvidence.fileCount} file{logEvidence.fileCount === 1 ? '' : 's'} · authenticated · Boswell
          Log.pdf is the primary open-hole exhibit
        </span>
      </div>
      <p className="chart-subtitle" style={{ marginBottom: 12 }}>
        Boswell has no digital LAS — its log lives as a raster PDF in the VDR. Files stream from the
        private host to signed-in desk users only; binaries never enter the repo or the public bundle.
      </p>
      <DataTable
        columns={[
          { key: 'priority', label: 'Pri' },
          { key: 'well', label: 'Well' },
          { key: 'field', label: 'Field' },
          { key: 'kind', label: 'Kind', format: (v) => KIND_LABEL[v] || v },
          { key: 'fileName', label: 'File' },
          {
            key: 'id',
            label: 'View',
            format: (v, row) => (
              <button type="button" className="btn-secondary" onClick={() => openEvidence(row)}>
                Open
              </button>
            ),
          },
        ]}
        data={files}
        defaultSort="priority"
        defaultDir="asc"
      />
      {msg && (
        <p className="engine-note" style={{ marginTop: 10 }}>
          {msg}
        </p>
      )}
    </section>
  );
}

export default function Geoscience({ data }) {
  const { setPageExtras, clearPageExtras } = useAssistantPageExtras();
  const { deal } = useDeal();
  const index = data;
  const files = index?.files || [];
  const wells = index?.wells || [];
  const [wellFilter, setWellFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [curveData, setCurveData] = useState(null);
  const [curveError, setCurveError] = useState(null);
  const [loadingCurves, setLoadingCurves] = useState(false);

  const filtered = useMemo(() => {
    return files.filter((f) => {
      if (wellFilter !== 'all' && f.wellFolder !== wellFilter) return false;
      if (kindFilter !== 'all' && f.kind !== kindFilter) return false;
      return true;
    });
  }, [files, wellFilter, kindFilter]);

  // Default select first plottable file
  useEffect(() => {
    if (selectedId) return;
    const first = filtered.find((f) => f.hasDisplay) || filtered[0];
    if (first) setSelectedId(first.id);
  }, [filtered, selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    const meta = files.find((f) => f.id === selectedId);
    if (!meta?.hasDisplay) {
      setCurveData(null);
      setCurveError('No downsampled curves for this file (wrap LAS, survey-only, or parse skip).');
      return;
    }
    let cancelled = false;
    setLoadingCurves(true);
    setCurveError(null);
    fetch(dealDataUrl(deal, `las-curves/${selectedId}.json`, import.meta.env.BASE_URL), {
      credentials: 'include',
    })
      .then((r) => {
        if (!r.ok) throw new Error(`Curve JSON ${r.status}`);
        return r.json();
      })
      .then((payload) => {
        if (!cancelled) {
          setCurveData(payload);
          track('las_viewed', {
            las_id: selectedId,
            well: payload.wellName,
            kind: payload.kind,
            curves: Object.keys(payload.curves || {}),
          });
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setCurveData(null);
          setCurveError(e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingCurves(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, files, deal]);

  const selected = files.find((f) => f.id === selectedId);

  useEffect(() => {
    setPageExtras({
      tab: 'geoscience',
      lasFileCount: index?.fileCount ?? 0,
      lasWellCount: index?.wellCount ?? 0,
      boswellLasCount: index?.boswellLasCount ?? 0,
      possiblePayFileCount: index?.lasFlags?.possiblePayFileCount ?? 0,
      cblLasCount: index?.lasFlags?.cblLasCount ?? 0,
      logEvidenceCount: index?.logEvidence?.fileCount ?? 0,
      selectedLas: selected
        ? {
            id: selected.id,
            well: selected.wellFolder,
            kind: selected.kind,
            fileName: selected.fileName,
            preferred: selected.preferred,
          }
        : null,
    });
    return () => clearPageExtras();
  }, [index, selected, setPageExtras, clearPageExtras]);

  if (!index?.files?.length) {
    return (
      <div className="dashboard-page">
        <div className="page-header">
          <div className="page-header-left">
            <h2 className="page-title">Geoscience · Well logs</h2>
            <p className="page-subtitle">No LAS index — run scripts/index_las_files.py then npm run generate-data.</p>
          </div>
        </div>
        <EvidenceLogs logEvidence={index?.logEvidence} />
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Geoscience · Well logs</h2>
          <p className="page-subtitle">
            VDR LAS inventory + curve viewer · Bald Prairie coverage · Boswell has no LAS in room
          </p>
          <div className="page-quote">
            <span className="page-quote-label">Evidence room</span>
            <span>
              &ldquo;{index.fileCount} LAS across {index.wellCount} wells — use for secondary-well geology;
              package value still hinges on Boswell DCA, not these logs.&rdquo;
            </span>
          </div>
        </div>
      </div>

      <KPIGrid>
        <KPICard label="LAS files" value={String(index.fileCount)} sub={index.source} color="var(--navy)" />
        <KPICard
          label="Wells with LAS"
          value={String(index.wellCount)}
          sub={`${Object.keys(index.kinds || {}).length} kinds`}
          color="var(--amber)"
        />
        <KPICard
          label="Plottable curves"
          value={String(files.filter((f) => f.hasDisplay).length)}
          sub="Downsampled GR / RHOB / NPHI / RT / …"
          color="var(--green)"
        />
        <KPICard
          label="Boswell LAS"
          value={String(index.boswellLasCount ?? 0)}
          sub="Gap — use DCA challenger on Sensitivity"
          color="var(--red)"
        />
      </KPIGrid>

      <section className="dashboard-section">
        <div className="charts-grid charts-grid-2">
          <div className="chart-card">
            <div className="chart-title">Inventory</div>
            <div className="chart-subtitle" style={{ marginBottom: 10 }}>
              Filters · click a row to load the log display
            </div>
            <div className="las-filters">
              <label>
                Well
                <select value={wellFilter} onChange={(e) => setWellFilter(e.target.value)}>
                  <option value="all">All wells</option>
                  {wells.map((w) => (
                    <option key={w.name} value={w.name}>
                      {w.name} ({w.lasCount})
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Kind
                <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}>
                  <option value="all">All kinds</option>
                  {Object.keys(index.kinds || {}).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABEL[k] || k} ({index.kinds[k]})
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <DataTable
              columns={[
                { key: 'wellFolder', label: 'Well' },
                {
                  key: 'kind',
                  label: 'Kind',
                  format: (v) => KIND_LABEL[v] || v,
                },
                { key: 'fileName', label: 'File' },
                {
                  key: 'preferred',
                  label: 'Curves',
                  format: (v) => (Array.isArray(v) && v.length ? v.join(', ') : '—'),
                },
                {
                  key: 'hasDisplay',
                  label: 'View',
                  format: (v) => (v ? 'Yes' : 'Meta'),
                },
              ]}
              data={filtered}
              defaultSort="wellFolder"
              onRowClick={(row) => setSelectedId(row.id)}
              rowClassName={(row) => (row.id === selectedId ? 'dtable-row-selected' : '')}
              maxHeight={420}
            />
          </div>

          <div className="chart-card">
            <div className="chart-title">
              {selected ? selected.wellName || selected.wellFolder : 'Log display'}
            </div>
            <div className="chart-subtitle">
              {selected
                ? `${selected.fileName} · ${KIND_LABEL[selected.kind] || selected.kind}${
                    selected.api ? ` · API ${selected.api}` : ''
                  }`
                : 'Select a LAS file'}
            </div>
            {loadingCurves && <p className="engine-note">Loading curves…</p>}
            {curveError && <p className="engine-note error">{curveError}</p>}
            {curveData?.depth?.length ? (
              <LasLogChart data={curveData} />
            ) : (
              !loadingCurves &&
              !curveError && <p className="engine-note">No curve series to plot.</p>
            )}
            {selected && (
              <p className="engine-note" style={{ marginTop: 10 }}>
                Source rows {selected.nRows?.toLocaleString?.() || selected.nRows} · preferred:{' '}
                {(selected.preferred || []).join(', ') || 'none'} · CA — curve JSON only (raw LAS not
                served).
              </p>
            )}
            {selected && (
              <button
                type="button"
                className="btn-secondary"
                style={{ marginTop: 10 }}
                onClick={() =>
                  navigateToWell({
                    api: selected.api,
                    lease: selected.wellName || selected.wellFolder,
                  })
                }
              >
                Open well detail →
              </button>
            )}
          </div>
        </div>
      </section>

      <LasFlags
        lasFlags={index?.lasFlags}
        onOpenLas={(id) => id && setSelectedId(id)}
        hasCurves={(id) => files.some((f) => f.id === id)}
      />

      <EvidenceLogs logEvidence={index?.logEvidence} />
    </div>
  );
}

/**
 * Secondary-well LAS pay / CBL flags (SPEC-56). Rule-based diligence screen —
 * NEVER a package-PV input. Every row is badged "Diligence only — not in PV".
 */
export function LasFlags({ lasFlags, onOpenLas, hasCurves }) {
  const wells = lasFlags?.wells || [];
  const filesFlagged = lasFlags?.files || [];
  if (!wells.length && !filesFlagged.length) return null;

  const fmtFt = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : `${Number(v).toFixed(0)} ft`);

  return (
    <section className="dashboard-section">
      <div className="section-title">
        LAS pay / CBL flags
        <span className="section-meta">
          {' '}
          · {lasFlags.possiblePayFileCount ?? 0} possible-pay · {lasFlags.cblLasCount ?? 0} CBL ·{' '}
          <span className="pill severity-low">Diligence only — not in PV</span>
        </span>
      </div>
      <p className="chart-subtitle" style={{ marginBottom: 12 }}>
        Rule-based screen ({lasFlags.method || 'GR / porosity / resistivity cutoffs'}). These flags do
        NOT change Decision PV or <code>boswellVolAdj</code> — Boswell has no LAS and is valued by DCA.
        Click a flagged file to open its curves in the viewer above.
      </p>
      <div className="charts-grid charts-grid-2">
        <div>
          <div className="chart-title" style={{ fontSize: 13 }}>By well</div>
          <DataTable
            columns={[
              { key: 'well', label: 'Well' },
              { key: 'lasCount', label: 'LAS' },
              { key: 'possiblePayFiles', label: 'Pay flags' },
              { key: 'netPayFt', label: 'Net pay', format: fmtFt },
              { key: 'hasCblLas', label: 'CBL', format: (v) => (v ? 'Yes' : '—') },
            ]}
            data={wells}
            defaultSort="possiblePayFiles"
            maxHeight={360}
          />
        </div>
        <div>
          <div className="chart-title" style={{ fontSize: 13 }}>By file</div>
          <DataTable
            columns={[
              { key: 'well', label: 'Well' },
              { key: 'fileName', label: 'File' },
              {
                key: 'hasPay',
                label: 'Possible pay',
                format: (v, row) =>
                  v ? `Yes · ${fmtFt(row.netPayFt)}` : row.reason?.includes('insufficient') ? 'n/a' : '—',
              },
              { key: 'hasCblLas', label: 'CBL', format: (v) => (v ? 'Yes' : '—') },
              {
                key: 'id',
                label: 'View',
                format: (v) =>
                  hasCurves?.(v) ? (
                    <button type="button" className="btn-secondary" onClick={() => onOpenLas?.(v)}>
                      Open
                    </button>
                  ) : (
                    '—'
                  ),
              },
            ]}
            data={filesFlagged}
            defaultSort="hasPay"
            maxHeight={360}
          />
        </div>
      </div>
    </section>
  );
}
